/**
 * GoalStore — the goals feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY goals module that talks to the API, and it
 * does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Goal>('goals')` + `api.identityProvider`). It never imports
 * ApiClient or calls fetch. React hooks call this store; pages call the hook.
 *
 * Persistence model: each goal is one document with id `goal_<uuid>` in the
 * real 'goals' generic-store collection (DDB pointer row + S3 body). The
 * list/tree-renderable fields (title, goalType, goalFramework, planningPeriod,
 * level, status, owningTeam, goalLead, leadershipSponsor, manualProgress,
 * parentGoalId, linked-count hints, createdBy, lastUpdated, deleted) are
 * mirrored onto the DDB pointer row's index attributes so the list and the
 * hierarchy tree render directly from the pointer rows — WITHOUT hydrating S3
 * bodies. The full narrative/advanced fields + link id arrays live in the S3
 * body, hydrated only on the detail view via {@link get}.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Goal, GoalDraft, GoalStatus, GoalSummary } from './types';
import { goalDocId, isGoalStatus, normalizeProgress } from './goalModel';

export const GOALS_COLLECTION = 'goals';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  title: 'title',
  goalType: 'goalType',
  goalFramework: 'goalFramework',
  planningPeriod: 'planningPeriod',
  level: 'level',
  status: 'status',
  owningTeam: 'owningTeam',
  goalLead: 'goalLead',
  leadershipSponsor: 'leadershipSponsor',
  manualProgress: 'manualProgress',
  parentGoalId: 'parentGoalId',
  linkedStoryCount: 'linkedStoryCount',
  linkedInitiativeCount: 'linkedInitiativeCount',
  createdBy: 'createdBy',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

export class GoalStore {
  private readonly docs: DocumentStore<Goal>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Goal>(GOALS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(goal: Goal): StringMap {
    return {
      [ROW.title]: goal.title,
      [ROW.goalType]: goal.goalType,
      [ROW.goalFramework]: goal.goalFramework,
      [ROW.planningPeriod]: goal.planningPeriod,
      [ROW.level]: goal.level,
      [ROW.status]: goal.status,
      [ROW.owningTeam]: goal.owningTeam ?? '',
      [ROW.goalLead]: goal.goalLead ?? '',
      [ROW.leadershipSponsor]: goal.leadershipSponsor ?? '',
      [ROW.manualProgress]:
        goal.manualProgress === undefined ? '' : String(goal.manualProgress),
      [ROW.parentGoalId]: goal.parentGoalId ?? '',
      [ROW.linkedStoryCount]: String((goal.linkedStoryIds ?? []).length),
      [ROW.linkedInitiativeCount]: String(
        (goal.linkedInitiativeIds ?? []).length,
      ),
      [ROW.createdBy]: goal.createdBy ?? '',
      [ROW.lastUpdated]: goal.lastUpdated ?? '',
      [ROW.deleted]: goal.deleted ? 'true' : 'false',
    };
  }

  /** Projects a raw pointer row into a lightweight summary. */
  private toSummary(row: StringMap): GoalSummary {
    const status = row[ROW.status] ?? '';
    const progressRaw = row[ROW.manualProgress];
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      title: row[ROW.title] ?? '',
      goalType: row[ROW.goalType] ?? '',
      goalFramework: row[ROW.goalFramework] ?? '',
      planningPeriod: row[ROW.planningPeriod] ?? '',
      level: row[ROW.level] ?? '',
      status: isGoalStatus(status) ? status : 'NOT_STARTED',
      owningTeam: row[ROW.owningTeam] ?? '',
      goalLead: row[ROW.goalLead] ?? '',
      leadershipSponsor: row[ROW.leadershipSponsor] ?? '',
      manualProgress: normalizeProgress(progressRaw),
      parentGoalId: row[ROW.parentGoalId] || undefined,
      linkedStoryCount: Number(row[ROW.linkedStoryCount] ?? '0') || 0,
      linkedInitiativeCount: Number(row[ROW.linkedInitiativeCount] ?? '0') || 0,
      createdBy: row[ROW.createdBy] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /**
   * Lists all non-deleted goal summaries. Filters to this collection
   * defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<GoalSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === GOALS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single goal document by id (hydrates the S3 body). */
  async get(id: string): Promise<Goal | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new goal. Seeds id, createdBy (from identity) and lastUpdated,
   * and returns the saved document.
   */
  async create(draft: GoalDraft): Promise<Goal> {
    const identity = await this.api.identityProvider.getIdentity();
    const goal = this.fromDraft(goalDocId(), draft, identity.alias);
    await this.docs.save(goal.id, goal, this.indexAttributes(goal));
    return goal;
  }

  /**
   * Applies a draft's fields to an existing goal in place (same id/createdBy),
   * refreshes lastUpdated, and re-mirrors index attributes.
   */
  async applyDraft(goal: Goal, draft: GoalDraft): Promise<Goal> {
    const updated = this.fromDraft(goal.id, draft, goal.createdBy);
    return this.update(updated);
  }

  /** Persists a full goal document in place. */
  async update(goal: Goal): Promise<Goal> {
    const updated: Goal = {
      ...goal,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Sets the effective status (+ optional note). This is the persisted human
   * override of the Goal Health recommendation.
   */
  async setStatus(
    goal: Goal,
    status: GoalStatus,
    statusNote?: string,
  ): Promise<Goal> {
    return this.update({
      ...goal,
      status,
      statusNote: statusNote ?? goal.statusNote,
    });
  }

  /**
   * Soft-deletes a goal: fetches it, flags it deleted, and updates both the S3
   * body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Goal = {
      ...existing,
      deleted: true,
      lastUpdated: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /** Builds a full Goal from a draft, seeding id/createdBy and lastUpdated. */
  private fromDraft(id: string, draft: GoalDraft, createdBy: string): Goal {
    return {
      id,
      title: draft.title.trim(),
      goalType: draft.goalType,
      goalFramework: draft.goalFramework,
      planningPeriod: draft.planningPeriod,
      level: draft.level,
      description: draft.description ?? '',
      whyItMatters: draft.whyItMatters ?? '',
      expectedOutcome: draft.expectedOutcome ?? '',
      successMetrics: draft.successMetrics ?? '',
      target: draft.target ?? '',
      manualProgress: normalizeProgress(draft.manualProgress),
      status: draft.status,
      statusNote: draft.statusNote?.trim() || undefined,
      owningTeam: draft.owningTeam?.trim() ?? '',
      goalLead: draft.goalLead?.trim() ?? '',
      pocs: draft.pocs ?? [],
      contributors: draft.contributors ?? [],
      leadershipSponsor: draft.leadershipSponsor?.trim() ?? '',
      supportingDocLink: draft.supportingDocLink?.trim() || undefined,
      relatedGoalIds: draft.relatedGoalIds ?? [],
      linkedInitiativeIds: draft.linkedInitiativeIds ?? [],
      linkedStoryIds: draft.linkedStoryIds ?? [],
      dependencies: draft.dependencies ?? [],
      risks: draft.risks ?? [],
      nextMilestone: draft.nextMilestone,
      parentGoalId: draft.parentGoalId || undefined,
      createdBy,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
  }
}
